<template>
   <Form as="div" ref="Form">
    <h3 v-html="title" />
    <p data-test-call-me-booking-date-description v-html="description" />
    <div class="control push-down">
      <label data-test-call-me-booking-date-label-date v-html="dateLabel" />
      <Field 
        name="BookingDate"
        :model-value="dateSelected"
        :rules="{ required: dateSelected === null }"
        :label="'Dato'"
      >
        <date-picker
          id="BookingDate"
          name="BookingDate"
          class="datepicker"
          data-vv-as="Dato"
          :min-date="currentDate"
          :max-date="twoWeeksFromNow"
          :disable-weekends="true"
          :disable-holidays="true"
          @date-changed="dateChanged"
        />
      </Field>
      <ValidationError name="BookingDate" />
    </div>
    <div v-if="dateSelected !== null" class="control push-down">
      <label data-test-call-me-booking-date-label-time v-html="timeLabel" />
      <Field
        name="BookingTime"
        :model-value="timeSelected"
        :rules="{ required: true }"
        :label="'Tid'"
      >
        <v-select
          id="BookingTime"
          name="BookingTime"
          v-model="timeSelected"
          data-vv-as="Tid"
          :options="timeSlots"
        />
      </Field>
      <ValidationError name="BookingTime" />
    </div>
    <div class="control">
      <button
        class="button large"
        data-test-callme-button
        @click="emitBookingDate"
        v-html="buttonLabel"
      />
    </div>
   </Form>
</template>

<script lang="ts">
import {
  DailyOpeningHours,
  WeeklyOpeningHours,
  CallMeBookingDateData,
  Choice
} from './types/CallMe';
import DatePicker from '@/components/UI/Pickadate.vue';
import vSelect from '@/components/UI/VueSelect.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import { defineComponent, PropType } from 'vue';
import { Form, ValidationResult } from 'vee-validate';

export default defineComponent({
  name: 'CallMeBookingDate',
  components: {
    ValidationError,
    DatePicker,
    vSelect
  },
  props: {
    title: {
      type: String,
      default() {
        return 'Vælg tidspunkt for opkald';
      }
    },
    description: {
      type: String,
      default() {
        return '<p>Du kan booke en aftale 14 dage frem.</p>';
      }
    },
    dateLabel: {
      type: String,
      default() {
        return 'Dato';
      }
    },
    timeLabel: {
      type: String,
      default() {
        return 'Jeg vil gerne ringes op imellem...';
      }
    },
    interval: {
      type: Number,
      default() {
        return 14;
      }
    },
    openHours: {
      type: Object as PropType<WeeklyOpeningHours>,
      default() {
        return {
          monday: {
            open: '09:30:00',
            close: '18:00:00'
          },
          tuesday: {
            open: '09:30:00',
            close: '18:00:00'
          },
          wednesday: {
            open: '09:30:00',
            close: '18:00:00'
          },
          thursday: {
            open: '09:30:00',
            close: '18:00:00'
          },
          friday: {
            open: '10:00:00',
            close: '16:00:00'
          },
          saturday: {
            open: null,
            close: null
          },
          sunday: {
            open: null,
            close: null
          }
        };
      }
    },
    buttonLabel: {
      type: String,
      default() {
        return 'Næste';
      }
    }
  },
  data(): CallMeBookingDateData {
    return {
      currentDate: new Date(),
      dateSelected: null,
      timeSelected: null,
      timeslotsSalesforce: null,
    };
  },
  computed: {
    twoWeeksFromNow(): Date {
      const currentTime = new Date();
      const futureInterval =
        this.interval === null || typeof this.interval === 'undefined' ? 14 : this.interval;
      currentTime.setDate(currentTime.getDate() + futureInterval);
      return currentTime;
    },
    timeSlots(): Choice[] | null {
      if (this.dateSelected !== null) {
        const weekDay = this.dateSelected.getDay();
        const danishWeekDay = weekDay === 0 ? 6 : weekDay - 1;
        const danishWeekDayString = Object.keys(this.openHours)[
          danishWeekDay
        ] as keyof WeeklyOpeningHours;
        return this.makeTimeSlots(this.openHours[danishWeekDayString]);
      }
      return null;
    }
  },
  methods: {
    emitBookingDate(): void {
      (this.$refs.Form as typeof Form).validate().then((result: ValidationResult) => {
        if (!result.valid) return;
        const dateAndTime = {
          date: this.dateSelected,
          time: this.timeSelected
        };
        this.$emit('booking-date', dateAndTime);
      });
    },
    makeTimeSlots(dayHours: DailyOpeningHours | null): Choice[] | null {
      this.timeSelected = null;
      (this.$refs.Form as typeof Form).setErrors({});

      const halfHourSlots: Choice[] = [];

      const randomDate = '2025-01-01';
      const startTime = dayHours?.open;
      const endTime = dayHours?.close;
      const startDate = new Date(`${randomDate} ${startTime}`);
      const endDate = new Date(`${randomDate} ${endTime}`);

      if (startDate >= endDate || dayHours === null) {
        return null;
      }

      while (startDate < endDate) {
        const slotEndTime = new Date(startDate.getTime() + 30 * 60000);
        const slotStart = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const slotEnd = slotEndTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const slot = `Kl. ${slotStart} - ${slotEnd}`;
        const value = startDate.getHours() + startDate.getMinutes() / 60;
        const choice: Choice = { label: slot, value: value.toFixed(1) };
        halfHourSlots.push(choice);

        startDate.setMinutes(startDate.getMinutes() + 30);
      }

      return halfHourSlots;
    },
    dateChanged(param: Date) {
      this.dateSelected = param;
    },
  }
});
</script>
